// ─── World Prop Placement ──────────────────────────────────────────────────────
//
// Decorative prop layout for the 80x60 overworld. The renderer draws each prop
// depth-sorted with entities so characters can walk behind canopies; a sprite's
// bottom-center sits at the bottom of its anchor tile (tx, ty). world-map.ts
// imports WORLD_PROPS and marks every anchor tile as blocked.
//
// Placement is fully deterministic (hash-jittered border bands + hand-placed
// clusters), so the overworld is identical every session. The forbidden-zone
// geometry below mirrors the layout constants in src/engine/world-map.ts —
// if plots/paths move there, update the sets here too.

export interface WorldProp {
  key: string;
  tx: number;
  ty: number;
}

/** Collision footprint in tiles, centered on the anchor tile. */
export const PROP_FOOTPRINT: Record<string, { w: number; h: number }> = {
  prop_tree_large: { w: 1, h: 1 },
  prop_tree_small: { w: 1, h: 1 },
  prop_rock_1: { w: 1, h: 1 },
  prop_rock_2: { w: 1, h: 1 },
  prop_rock_3: { w: 1, h: 1 },
  prop_bush_1: { w: 1, h: 1 },
  prop_bush_2: { w: 1, h: 1 },
  prop_stump: { w: 1, h: 1 },
  prop_signpost: { w: 1, h: 1 },
  prop_flowerbed: { w: 1, h: 1 },
};

// ── Layout geometry (mirrors world-map.ts — keep in sync) ─────────────────────

const COLS = 80;
const ROWS = 60;

/** Vertical 2-wide stone paths. */
const PATH_COLS = new Set([9, 10, 24, 25, 39, 40, 54, 55, 69, 70]);
/** Horizontal main street. */
const STREET_ROWS = new Set([16, 17]);
/** Building plots: 10 wide x 8 tall at these top-left corners. */
const PLOT_COL_STARTS = [5, 20, 35, 50, 65];
const PLOT_ROW_STARTS = [8, 22];
/** Junction plazas (vertical path x street): cols pc-1..pc+2, rows 15..18. */
const JUNCTION_PATH_COLS = [9, 24, 39, 54, 69];
/** Bottom-row door plazas: cols dc-1..dc+2, rows 29..31. */
const DOOR_COLS = [10, 25, 40, 55, 70];

/** Inside a building plot or its 1-tile margin. */
function inBuildingZone(tx: number, ty: number): boolean {
  for (const rs of PLOT_ROW_STARTS) {
    if (ty < rs - 1 || ty > rs + 8) continue;
    for (const cs of PLOT_COL_STARTS) {
      if (tx >= cs - 1 && tx <= cs + 10) return true;
    }
  }
  return false;
}

/** True when a tile must stay free of generated props. */
function isBlocked(tx: number, ty: number): boolean {
  if (ty >= 34) return true; // boardwalk / beach / ocean
  if (PATH_COLS.has(tx)) return true;
  if (STREET_ROWS.has(ty)) return true;
  if (inBuildingZone(tx, ty)) return true;
  if (tx >= 25 && tx <= 38 && ty <= 8) return true; // pond clearing
  if (tx >= 20 && tx <= 30 && ty >= 13 && ty <= 23) return true; // spawn area
  for (const pc of JUNCTION_PATH_COLS) {
    if (tx >= pc - 1 && tx <= pc + 2 && ty >= 15 && ty <= 18) return true;
  }
  for (const dc of DOOR_COLS) {
    if (tx >= dc - 1 && tx <= dc + 2 && ty >= 29 && ty <= 31) return true;
  }
  return false;
}

/** Deterministic [0,1) hash — keeps the map identical across sessions. */
function jitter(n: number): number {
  let x = (n ^ 0x9e3779b9) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x045d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x045d9f3b) >>> 0;
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

// ── Builder ────────────────────────────────────────────────────────────────────

const props: WorldProp[] = [];
const used = new Set<string>();

/**
 * Add a prop. `force` bypasses the forbidden-zone check for hand-verified
 * accent placements (bushes by doors, beach rocks, plaza flowerbeds).
 */
function place(key: string, tx: number, ty: number, force = false): void {
  if (tx < 0 || tx >= COLS || ty < 0 || ty >= ROWS) return;
  if (!force && isBlocked(tx, ty)) return;
  const at = `${tx},${ty}`;
  if (used.has(at)) return;
  used.add(at);
  props.push({ key, tx, ty });
}

/** Asymmetric tree cluster: 1 large + 2 smalls offset diagonally. */
function treeCluster(tx: number, ty: number, flip = false): void {
  const d = flip ? -1 : 1;
  place("prop_tree_large", tx, ty);
  place("prop_tree_small", tx + d, ty + 1);
  place("prop_tree_small", tx - 2 * d, ty - 1);
}

/** Small rock outcrop: 3 varied rocks, optional stump. */
function rockOutcrop(tx: number, ty: number, withStump = false): void {
  place("prop_rock_1", tx, ty);
  place("prop_rock_2", tx + 1, ty + 1);
  place("prop_rock_3", tx - 1, ty + 1);
  if (withStump) place("prop_stump", tx + 2, ty);
}

// ── Border forest walls (dense, staggered — canopy bleeds off the map edge) ──

// Top wall: two staggered ranks across rows 0..3.
for (let c = 0; c < COLS; c++) {
  if (jitter(c * 7 + 1) < 0.55) {
    place(
      jitter(c * 31 + 9) < 0.6 ? "prop_tree_large" : "prop_tree_small",
      c,
      jitter(c * 13 + 2) < 0.5 ? 0 : 1,
    );
  }
  if (jitter(c * 11 + 3) < 0.38) {
    place(
      jitter(c * 11 + 3) < 0.17 ? "prop_tree_large" : "prop_tree_small",
      c,
      jitter(c * 17 + 4) < 0.5 ? 2 : 3,
    );
  }
}

// Left + right walls (rows 4..33; rows 0..3 already covered by the top wall).
for (let r = 4; r < 34; r++) {
  if (jitter(r * 19 + 5) < 0.5) {
    place(
      jitter(r * 29 + 8) < 0.55 ? "prop_tree_large" : "prop_tree_small",
      jitter(r * 37 + 6) < 0.5 ? 0 : 1,
      r,
    );
  }
  if (jitter(r * 23 + 7) < 0.28) place("prop_tree_small", 2, r);
  if (jitter(r * 41 + 10) < 0.5) {
    place(
      jitter(r * 43 + 11) < 0.55 ? "prop_tree_large" : "prop_tree_small",
      jitter(r * 47 + 12) < 0.5 ? 79 : 78,
      r,
    );
  }
  if (jitter(r * 53 + 13) < 0.28) place("prop_tree_small", 77, r);
}

// Tree/bush line above the boardwalk (paths keep their beach crossings open).
for (let c = 0; c < COLS; c++) {
  if (jitter(c * 59 + 14) < 0.4) {
    const roll = jitter(c * 61 + 15);
    const key =
      roll < 0.4
        ? "prop_tree_small"
        : roll < 0.7
          ? "prop_bush_1"
          : "prop_bush_2";
    place(key, c, jitter(c * 71 + 17) < 0.5 ? 32 : 33);
  }
}

// ── Interior tree clusters (asymmetric, framing meadows and corridors) ────────

treeCluster(13, 4);
treeCluster(5, 5, true);
treeCluster(44, 3, true);
treeCluster(59, 5);
treeCluster(74, 4, true);
treeCluster(17, 9);
treeCluster(16, 22, true);
treeCluster(32, 11, true);
treeCluster(33, 26);
treeCluster(47, 10);
treeCluster(46, 24, true);
treeCluster(62, 9, true);
treeCluster(63, 27);
treeCluster(4, 19);
treeCluster(14, 20, true);
treeCluster(45, 19);
treeCluster(60, 19, true);
treeCluster(75, 19);
treeCluster(76, 26, true);
treeCluster(6, 31);
treeCluster(20, 32, true);
treeCluster(35, 31);
treeCluster(49, 31, true);
treeCluster(65, 31);

// Lone trees softening the street shoulders.
place("prop_tree_small", 13, 18);
place("prop_tree_small", 16, 19);
place("prop_tree_small", 33, 18);
place("prop_tree_small", 44, 18);
place("prop_tree_small", 58, 18);
place("prop_tree_small", 64, 18);
place("prop_tree_small", 73, 18);

// ── Rock outcrops + stumps ─────────────────────────────────────────────────────

rockOutcrop(12, 5, true);
rockOutcrop(46, 5);
rockOutcrop(17, 27, true);
rockOutcrop(62, 19, true);
rockOutcrop(33, 19);
rockOutcrop(76, 9);
rockOutcrop(3, 28);
place("prop_stump", 15, 5);
place("prop_stump", 48, 21);
place("prop_stump", 8, 20);

// Beach rocks (hand-verified open sand; clear of pool, tiki bar, and palms).
place("prop_rock_2", 12, 42, true);
place("prop_rock_1", 13, 43, true);
place("prop_rock_3", 20, 46, true);
place("prop_rock_1", 68, 44, true);
place("prop_rock_3", 69, 45, true);
place("prop_rock_2", 75, 48, true);

// ── Bushes flanking building entrances ─────────────────────────────────────────

// Bottom-row buildings: door plazas at rows 30-31 — bushes hug the plaza edges.
place("prop_bush_1", 8, 30, true);
place("prop_bush_2", 13, 30, true);
place("prop_bush_2", 23, 30, true);
place("prop_bush_1", 28, 30, true);
place("prop_bush_1", 38, 30, true);
place("prop_bush_2", 43, 30, true);
place("prop_bush_2", 53, 30, true);
place("prop_bush_1", 58, 30, true);
place("prop_bush_1", 68, 30, true);
place("prop_bush_2", 73, 30, true);

// Top-row buildings: bushes at the grass corners beside each facade (row 15).
place("prop_bush_1", 4, 15, true);
place("prop_bush_2", 15, 15, true);
place("prop_bush_1", 19, 15, true);
place("prop_bush_2", 30, 15, true);
place("prop_bush_1", 34, 15, true);
place("prop_bush_2", 45, 15, true);
place("prop_bush_1", 49, 15, true);
place("prop_bush_2", 60, 15, true);
place("prop_bush_1", 64, 15, true);
place("prop_bush_2", 75, 15, true);

// ── Signposts at path junctions + flowerbeds near plazas ─────────────────────

place("prop_signpost", 12, 18, true);
place("prop_signpost", 27, 18, true); // beside the player spawn plaza
place("prop_signpost", 42, 18, true);
place("prop_signpost", 57, 18, true);
place("prop_signpost", 72, 18, true);
place("prop_signpost", 8, 33, true); // beach approach

place("prop_flowerbed", 7, 18, true);
place("prop_flowerbed", 13, 19, true);
place("prop_flowerbed", 22, 19, true);
place("prop_flowerbed", 28, 20, true);
place("prop_flowerbed", 37, 18, true);
place("prop_flowerbed", 42, 19, true);
place("prop_flowerbed", 52, 18, true);
place("prop_flowerbed", 57, 19, true);
place("prop_flowerbed", 67, 18, true);
place("prop_flowerbed", 72, 19, true);
place("prop_flowerbed", 26, 1, true); // pond clearing, west bank
place("prop_flowerbed", 38, 6, true); // pond clearing, east bank
place("prop_flowerbed", 29, 37, true); // pool corner
place("prop_flowerbed", 42, 43, true); // pool corner

export const WORLD_PROPS: WorldProp[] = props;
